import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { createInvite, createTeam, listInvites, revokeInvite, sendInviteEmail, setMemberLanguages, updateMember } from '../lib/api';
import { dateTime, friendlyError, roleLabel, copyText } from '../lib/format';
import type { Invite, Member, Role } from '../lib/types';
import { Badge, Button, ErrorText, Modal, Notice, PageHeader, SelectField, TextField } from '../components/ui';

export function Team() {
  const { members, teams, me, isManager, reload } = useAuth();
  const [invites, setInvites] = useState<Invite[]>([]);
  const [editing, setEditing] = useState<Member | null>(null);
  const [inviting, setInviting] = useState(false);
  const [newTeam, setNewTeam] = useState('');
  const [error, setError] = useState('');

  const loadInvites = useCallback(async () => { if (isManager) setInvites(await listInvites().catch(() => [])); }, [isManager]);
  useEffect(() => { loadInvites(); }, [loadInvites]);

  const teamName = (id: string | null, role?: Role) => teams.find((t) => t.id === id)?.name ?? (role === 'owner' || role === 'manager' ? 'All teams' : 'No team');

  async function addTeam(e: FormEvent) {
    e.preventDefault();
    setError('');
    try { await createTeam(newTeam); setNewTeam(''); await reload(); } catch (err) { setError(friendlyError(err)); }
  }

  return (
    <div>
      <PageHeader title="Team" subtitle={`${members.filter((m) => m.active).length} active people`}
        action={isManager && <Button variant="primary" onClick={() => setInviting(true)}>Invite someone</Button>} />

      <ul className="mb-8 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-surface">
        {members.map((m) => (
          <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
            <div className="min-w-0">
              <p className="font-medium">{m.full_name}{m.id === me?.id ? ' (you)' : ''}</p>
              <p className="text-sm text-muted">{roleLabel[m.role]} · {teamName(m.team_id, m.role)}{m.languages.length ? ` · ${m.languages.join(', ')}` : ''}</p>
            </div>
            <div className="flex items-center gap-2">
              {!m.active && <Badge tone="bad">Inactive</Badge>}
              {m.on_leave && <Badge tone="warn">On leave</Badge>}
              {(isManager || m.id === me?.id) && <Button onClick={() => setEditing(m)}>{m.id === me?.id && !isManager ? 'My leave' : 'Edit'}</Button>}
            </div>
          </li>
        ))}
      </ul>

      {isManager && (
        <>
          <h2 className="mb-2 font-display text-lg">Pending invites</h2>
          {invites.length === 0 ? <p className="mb-8 text-sm text-muted">No pending invites.</p> : (
            <ul className="mb-8 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-surface">
              {invites.map((i) => (
                <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{i.email}</p>
                    <p className="text-sm text-muted">{roleLabel[i.role]} · expires {dateTime(i.expires_at)}</p>
                  </div>
                  <div className="flex gap-2">
                    <EmailAgain token={i.token} />
                    <CopyLink token={i.token} />
                    <Button variant="ghost" onClick={async () => { await revokeInvite(i.id); await loadInvites(); }}>Revoke</Button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <h2 className="mb-2 font-display text-lg">Teams</h2>
          <div className="mb-3 flex flex-wrap gap-2">
            {teams.length === 0 ? <p className="text-sm text-muted">No teams yet. Teams group reps by project area, for example "Dwarka Expressway".</p>
              : teams.map((t) => <Badge key={t.id}>{t.name}</Badge>)}
          </div>
          <form onSubmit={addTeam} className="flex max-w-[460px] gap-2">
            <input aria-label="New team name" required value={newTeam} onChange={(e) => setNewTeam(e.target.value)} placeholder="New team name"
              className="min-w-0 flex-1 rounded-lg border border-line px-3 py-2 text-sm outline-none focus:border-accent" />
            <Button type="submit">Add team</Button>
          </form>
          <div className="mt-2"><ErrorText>{error}</ErrorText></div>
        </>
      )}

      <InviteModal open={inviting} onClose={() => setInviting(false)} onCreated={loadInvites} />
      {editing && <EditMemberModal member={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await reload(); }} />}
    </div>
  );
}

function inviteUrl(token: string) {
  return `${window.location.origin}/invite/${token}`;
}

function emailResult(r: { sent: boolean; how?: string; reason?: string }, email: string): string {
  if (r.sent) return r.how === 'sign_in_link' ? `Email sent to ${email} (they already have an account, so it is a sign-in link to the invite).` : `Invitation email sent to ${email}.`;
  const reason = r.reason ?? 'unknown error';
  if (/not.?authori[sz]ed|email_address_not_authorized/i.test(reason)) return `The email could not be sent: this app's email sender is not set up for outside addresses yet. Copy the link below and send it on WhatsApp.`;
  if (/rate limit/i.test(reason)) return 'Too many emails in the last hour. Copy the link below and send it on WhatsApp, or try again later.';
  return `The email could not be sent (${reason}). Copy the link below and send it on WhatsApp.`;
}

function EmailAgain({ token }: { token: string }) {
  const [state, setState] = useState('');
  return (
    <Button title={state || undefined} onClick={async () => { setState('Sending…'); const r = await sendInviteEmail(token); setState(r.sent ? 'Sent' : 'Not sent'); }}>
      {state || 'Email again'}
    </Button>
  );
}

function CopyLink({ token }: { token: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button onClick={async () => { await copyText(inviteUrl(token)); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
      {copied ? 'Copied' : 'Copy link'}
    </Button>
  );
}

function InviteModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const { teams, me } = useAuth();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('rep');
  const [teamId, setTeamId] = useState('');
  const [token, setToken] = useState('');
  const [mailNote, setMailNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const roles: Role[] = me?.role === 'owner' ? ['rep', 'reviewer', 'manager', 'owner'] : ['rep', 'reviewer'];

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const t = await createInvite(email, role, teamId || null);
      setMailNote('Sending the invitation email…'); setToken(t); onCreated();
      setMailNote(emailResult(await sendInviteEmail(t), email));
    } catch (err) { setError(friendlyError(err)); } finally { setBusy(false); }
  }
  const close = () => { setEmail(''); setToken(''); setMailNote(''); setError(''); onClose(); };

  return (
    <Modal open={open} onClose={close} title="Invite someone">
      {token ? (
        <div className="flex flex-col gap-3">
          <Notice>{mailNote}</Notice>
          <p className="text-sm text-muted">The link works once, for {email}, for 7 days. You can also send it yourself on WhatsApp.</p>
          <code className="break-all rounded-lg bg-surface2 px-3 py-2 font-mono text-xs">{inviteUrl(token)}</code>
          <div className="flex justify-end gap-2"><CopyLink token={token} /><Button variant="primary" onClick={close}>Done</Button></div>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <TextField label="Their email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <SelectField label="Role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {roles.map((r) => <option key={r} value={r}>{roleLabel[r]}</option>)}
          </SelectField>
          <SelectField label="Team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
            <option value="">No team</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </SelectField>
          <ErrorText>{error}</ErrorText>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={close}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Creating…' : 'Create invite'}</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function EditMemberModal({ member, onClose, onSaved }: { member: Member; onClose: () => void; onSaved: () => void }) {
  const { teams, me, isManager } = useAuth();
  const [role, setRole] = useState<Role>(member.role);
  const [teamId, setTeamId] = useState(member.team_id ?? '');
  const [onLeave, setOnLeave] = useState(member.on_leave);
  const [active, setActive] = useState(member.active);
  const [langs, setLangs] = useState(member.languages.join(', '));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const self = member.id === me?.id;
  const roles: Role[] = me?.role === 'owner' ? ['rep', 'reviewer', 'manager', 'owner'] : ['rep', 'reviewer'];
  const canEditRole = isManager && !self && (me?.role === 'owner' || !['owner', 'manager'].includes(member.role));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await updateMember({ id: member.id, role, team_id: teamId || null, on_leave: onLeave, active });
      const list = langs.split(',').map((x) => x.trim()).filter(Boolean);
      if (list.join(',') !== member.languages.join(',')) await setMemberLanguages(member.id, list);
      onSaved();
    } catch (err) { setError(friendlyError(err)); setBusy(false); }
  }

  return (
    <Modal open onClose={onClose} title={member.full_name}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        {canEditRole && (
          <SelectField label="Role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {Array.from(new Set([...roles, member.role])).map((r) => <option key={r} value={r}>{roleLabel[r]}</option>)}
          </SelectField>
        )}
        {isManager && (
          <SelectField label="Team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
            <option value="">No team</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </SelectField>
        )}
        <TextField label="Languages" value={langs} onChange={(e) => setLangs(e.target.value)} placeholder="Hindi, English, Punjabi"
          hint="Buyers who write in a language go to a rep who speaks it, when one is free." />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={onLeave} onChange={(e) => setOnLeave(e.target.checked)} />
          On leave (gets no new leads)
        </label>
        {isManager && !self && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Active (unticking removes their access at once; their leads stay with the firm)
          </label>
        )}
        <ErrorText>{error}</ErrorText>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
        </div>
      </form>
    </Modal>
  );
}
